import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core'
import { MessageService } from 'primeng/api'

import { WorklogService, worklogErrorMessage } from '../../data/services/worklog.service'
import { BOARD_COLUMNS, REQUIRES_POINTS_ON, TODO_COLUMN } from '../../data/constants/worklog.constants'
import { SprintSummary } from '../../data/interfaces/metrics.interface'
import { Sprint, WorkItem } from '../../data/interfaces/worklog.interface'
import { normalizeFolio } from '../../data/utils/folio.util'

const MS_DAY = 86400000

/**
 * La cadencia del equipo: **dos semanas, de lunes a viernes**. Un sprint que arranca el lunes
 * termina el viernes de la semana siguiente, once días después — no catorce. Los fines de semana
 * están dentro de la ventana pero no son días de trabajo, y cerrar en domingo obligaba a que
 * alguien se acordara de mover la fecha cada quincena.
 */
const SPRINT_DIAS = 11

/** El lunes de la semana de una fecha. Domingo cuenta como el de la semana que arranca al día siguiente. */
function lunesDe(d: Date): Date {
	const x = new Date(d.getFullYear(), d.getMonth(), d.getDate())
	const dow = x.getDay() //* 0 = domingo
	x.setDate(x.getDate() - (dow === 0 ? -1 : dow - 1))
	return x
}

const masDias = (d: Date, n: number) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n)

//* La aplicación no registra el locale `es`, así que el pipe `date` escribe los días y los meses
//* en inglés («Monday 31 Aug»). En un diálogo que se lee en español eso canta, y registrar el
//* locale global es un cambio de alcance mucho mayor que escribir dos arreglos.
const DIAS = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado']
const MESES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre']
const enEspanol = (d: Date) => `${DIAS[d.getDay()]} ${d.getDate()} de ${MESES[d.getMonth()]}`

const STATE_LABEL: Record<string, string> = { planned: 'Planeado', active: 'En curso', closed: 'Cerrado' }
const STATE_ICON: Record<string, string> = { planned: 'fa-calendar-plus', active: 'fa-circle-play', closed: 'fa-flag-checkered' }

/**
 * Cabecera del sprint, sobre las columnas del Tablero. Sustituye a la pestaña «Sprints», que
 * obligaba a salir del tablero para ver a qué sprint pertenecía lo que estabas mirando y en qué
 * iba: el sprint es el marco del trabajo, no un tema aparte.
 *
 * Muestra lo que se consulta a diario —estado, ventana de fechas, objetivo y carga— y guarda
 * detrás del engrane lo que se toca cada dos semanas: fechas, temáticas y el cierre del ciclo.
 */
@Component({
  selector: 'app-worklog-sprint-bar',
  templateUrl: './worklog-sprint-bar.component.html',
  styleUrls: ['./worklog-sprint-bar.component.scss'],
})
export class WorklogSprintBarComponent implements OnChanges {
  @Input() sprint: Sprint | null = null
  @Input() isDev = false
  /**
   * Las columnas del tablero, para poder decir QUÉ se arrastra al cerrar. Vienen del padre porque
   * ya las tiene cargadas: pedirlas otra vez sería una consulta para un dato que está en pantalla.
   */
  @Input() columns: Record<string, WorkItem[]> = {}
  /** Un cambio de sprint (activar, cerrar, editar) obliga a recargar el tablero entero. */
  @Output() changed = new EventEmitter<void>()
  /** Id de la tarea que el buscador por folio encontró; el padre abre su detalle. */
  @Output() openItem = new EventEmitter<string>()

  summary: SprintSummary | null = null

  configVisible = false
  saving = false
  /** Confirmación en dos pasos para lo irreversible: activar congela el compromiso, cerrar sella. */
  confirming: 'activate' | 'close' | null = null

  form = { name: '', goal: '', startDate: null as Date | null, endDate: null as Date | null }

  /**
   * Qué hacer al cerrar con lo que espera en To Deploy. Arranca SIN elegir a propósito: casi
   * siempre ya está en producción —el deploy ocurre y nadie mueve las tarjetas—, pero liberar es
   * irreversible, y un valor por defecto convertiría la confirmación en un clic sin leer.
   */
  liberarAlCerrar: boolean | null = null

  folioQuery = ''
  buscandoFolio = false

  readonly SPRINT_DIAS = SPRINT_DIAS

  constructor(private worklog: WorklogService, private messageService: MessageService) {}

  ngOnChanges(): void {
    if (!this.sprint) return
    this.worklog.getSprintSummary(this.sprint._id).subscribe((s) => (this.summary = s))
  }

  // ─────────── Lectura ───────────

  /** Días que faltan para el cierre. Negativo = el sprint ya se pasó de su ventana. */
  get daysLeft(): number | null {
    if (!this.sprint?.endDate) return null
    const fin = new Date(this.sprint.endDate).getTime()
    return Math.ceil((fin - Date.now()) / 86400000)
  }

  /** Un sprint que todavía no arranca: su ventana es una promesa, no una cuenta regresiva. */
  get esPlaneado(): boolean {
    return this.sprint?.status === 'planned'
  }

  /** Días que faltan para que ARRANQUE. Solo tiene sentido antes de activarlo. */
  get daysToStart(): number | null {
    if (!this.sprint?.startDate) return null
    const ini = new Date(this.sprint.startDate).getTime()
    return Math.ceil((ini - Date.now()) / MS_DAY)
  }

  get daysLabel(): string {
    //* Un sprint planeado no tiene días restantes: no ha empezado. Decir «11d restantes» sobre
    //* algo que aún no arranca es la clase de dato que se lee mal justo cuando importa.
    if (this.esPlaneado) {
      const d = this.daysToStart
      if (d === null) return 'sin arrancar'
      if (d > 0) return `arranca en ${d}d`
      return 'listo para arrancar'
    }
    const d = this.daysLeft
    if (d === null) return ''
    if (d < 0) return `${Math.abs(d)}d de más`
    if (d === 0) return 'cierra hoy'
    return `${d}d restantes`
  }

  /**
   * Lo que se congelará como compromiso al activar: la suma de los estimados de lo que apunta al
   * sprint. No viene del resumen porque `committedPoints` nace nulo y solo se llena al activar —
   * que es justo lo que este número existe para poder decidir.
   */
  get cargaPlaneada(): { puntos: number; tareas: number; sinEstimar: number } {
    let puntos = 0
    let tareas = 0
    let sinEstimar = 0
    for (const items of Object.values(this.columns ?? {})) {
      for (const it of items ?? []) {
        tareas++
        const e = it.estimatedPoints
        if (e == null) sinEstimar++
        else puntos += e
      }
    }
    return { puntos, tareas, sinEstimar }
  }

  /** % de avance sobre el compromiso. Sin línea base no hay barra que pintar. */
  get progressPct(): number | null {
    const s = this.summary
    if (!s?.committedPoints) return null
    return Math.min(100, Math.round((s.deliveredPoints / s.committedPoints) * 100))
  }


  // ─────────── Configuración ───────────

  /** El diálogo dice a qué sprint le está cambiando la configuración, no solo que la cambia. */
  get configTitle(): string {
    return this.sprint ? `Configurar el Sprint ${this.sprint.number}` : 'Configurar el sprint'
  }

  /** Duración de la ventana, contando ambos extremos: 17→30 de agosto son 14 días, no 13. */
  get rangeDays(): number | null {
    const { startDate, endDate } = this.form
    if (!startDate || !endDate) return null
    return Math.round((endDate.getTime() - startDate.getTime()) / 86400000) + 1
  }

  /** Se calcula a la vista para no obligar a contar días en el calendario. */
  get rangeLabel(): string {
    const d = this.rangeDays
    if (d === null || d < 1) return ''
    return d === 1 ? '1 día' : `${d} días`
  }

  /** Una ventana invertida deja el sprint sin burndown y los días restantes en negativo. */
  /**
   * El fin se DERIVA del inicio. La cadencia es fija —dos semanas, lunes a viernes— así que
   * teclear la fecha de cierre era una oportunidad de equivocarse sin ninguna decisión detrás.
   * El inicio además se acomoda al lunes de su semana: elegir un miércoles ya no crea un sprint
   * torcido que después descuadra la comparación entre quincenas.
   */
  onStartChange(d: Date | null): void {
    if (!d) {
      this.form.endDate = null
      this.rawStart = 0
      return
    }
    this.rawStart = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()
    const lunes = lunesDe(d)
    this.form.startDate = lunes
    this.form.endDate = masDias(lunes, SPRINT_DIAS)
  }

  //* El ajuste al lunes se AVISA. Corregir la fecha en silencio deja a quien planeó creyendo que
  //* eligió otra cosa, y lo descubre dos semanas después en el reporte.
  private rawStart = 0
  get startAdjusted(): boolean {
    return !!this.rawStart && !!this.form.startDate && this.form.startDate.getTime() !== this.rawStart
  }

  /** El fin como se lee en el diálogo. */
  get finLabel(): string {
    return this.form.endDate ? enEspanol(this.form.endDate) : '—'
  }

  /**
   * Un sprint guardado antes de que la cadencia existiera puede arrancar en martes y cerrar en
   * domingo. El campo se sigue llamando «cierra el viernes», así que hay que decirlo en vez de
   * pintar un domingo debajo de esa etiqueta — y ofrecer el arreglo, que es un clic.
   */
  get fueraDeCadencia(): boolean {
    const { startDate, endDate } = this.form
    if (!startDate || !endDate) return false
    return startDate.getDay() !== 1 || endDate.getTime() !== masDias(startDate, SPRINT_DIAS).getTime()
  }

  get cadenciaPropuesta(): { inicio: Date; fin: Date } | null {
    if (!this.form.startDate) return null
    const inicio = lunesDe(this.form.startDate)
    return { inicio, fin: masDias(inicio, SPRINT_DIAS) }
  }

  ajustarCadencia(): void {
    if (this.form.startDate) this.onStartChange(this.form.startDate)
  }

  get rangeError(): string | null {
    const { startDate, endDate } = this.form
    if (!startDate || !endDate) return null
    return endDate.getTime() < startDate.getTime() ? 'El fin no puede ser anterior al inicio.' : null
  }

  /** Ventana del sprint que viene: el lunes siguiente al viernes de cierre. */
  get proximaVentana(): { inicio: string; fin: string } | null {
    const fin = this.form.endDate ?? (this.sprint?.endDate ? new Date(this.sprint.endDate) : null)
    if (!fin) return null
    const inicio = lunesDe(masDias(fin, 3)) //* viernes + 3 = lunes
    return { inicio: enEspanol(inicio), fin: enEspanol(masDias(inicio, SPRINT_DIAS)) }
  }

  /**
   * Qué pasa al cerrar, contado sobre lo que hay en el tablero AHORA. Lo terminado se queda con
   * el sprint que lo entregó; lo demás se arrastra, y son dos cosas distintas: lo que ya arrancó
   * y lo que ni siquiera se tocó.
   */
  get cierre(): { terminadas: number; enCurso: number; porHacer: number; porLiberar: number } {
    const porLiberar = this.columns?.[REQUIRES_POINTS_ON]?.length ?? 0
    const enCurso = BOARD_COLUMNS.reduce((n, c) => n + (this.columns?.[c]?.length ?? 0), 0) - porLiberar
    return {
      terminadas: this.summary?.deliveredCount ?? 0,
      enCurso,
      porHacer: this.columns?.[TODO_COLUMN]?.length ?? 0,
      porLiberar,
    }
  }

  /** Cerrar exige decidir qué pasa con To Deploy cuando hay algo ahí. */
  get puedeConfirmar(): boolean {
    return this.confirming !== 'close' || !this.cierre.porLiberar || this.liberarAlCerrar !== null
  }

  get stateLabel(): string {
    return STATE_LABEL[this.sprint?.status ?? ''] ?? '—'
  }

  get stateIcon(): string {
    return STATE_ICON[this.sprint?.status ?? ''] ?? 'fa-circle'
  }

  /** Dice qué implica el siguiente paso ANTES de pedirlo: ambos son irreversibles. */
  get stateHint(): string {
    switch (this.sprint?.status) {
      case 'planned':
        return 'Al activarlo, su compromiso queda congelado como línea base.'
      case 'active':
        return 'Al cerrarlo se sellan sus métricas y lo abierto se arrastra al siguiente.'
      default:
        return 'Un sprint cerrado no se reabre; sus métricas quedan selladas.'
    }
  }

  openConfig(): void {
    if (!this.sprint) return
    this.confirming = null
    this.liberarAlCerrar = null
    this.form = {
      name: this.sprint.name ?? '',
      goal: (this.sprint as any).goal ?? '',
      startDate: this.sprint.startDate ? new Date(this.sprint.startDate) : null,
      endDate: this.sprint.endDate ? new Date(this.sprint.endDate) : null,
    }
    this.configVisible = true
  }

  save(): void {
    if (!this.sprint) return
    this.saving = true
    this.worklog
      .updateSprint(this.sprint._id, {
        name: this.form.name.trim() || null,
        goal: this.form.goal.trim() || null,
        startDate: this.form.startDate?.toISOString(),
        endDate: this.form.endDate?.toISOString(),
      } as any)
      .subscribe({
        next: () => {
          this.saving = false
          this.configVisible = false
          this.toast('success', 'Sprint actualizado', 'Los cambios ya están en el tablero.')
          this.changed.emit()
        },
        error: (e) => {
          this.saving = false
          this.toast('error', 'No se pudo guardar', worklogErrorMessage(e))
        },
      })
  }

  /**
   * Cerrar el sprint sella sus métricas y arrastra lo pendiente al siguiente; activar congela el
   * compromiso. Ninguna de las dos se deshace, así que piden confirmación explícita.
   */
  confirm(): void {
    if (!this.sprint || !this.confirming || !this.puedeConfirmar) return
    const status = this.confirming === 'activate' ? 'active' : 'closed'
    const releasePending = status === 'closed' && this.liberarAlCerrar === true
    this.saving = true
    this.worklog.updateSprint(this.sprint._id, { status, ...(releasePending ? { releasePending } : {}) } as any).subscribe({
      next: () => {
        this.saving = false
        this.configVisible = false
        this.confirming = null
        const detalleCierre = releasePending ? 'Lo de To Deploy pasó a Terminadas y lo pendiente se arrastró al siguiente.' : 'Lo pendiente se arrastró al siguiente.'
        this.toast('success', status === 'active' ? 'Sprint activado' : 'Sprint cerrado', status === 'active' ? 'El compromiso quedó congelado.' : detalleCierre)
        this.changed.emit()
      },
      error: (e) => {
        this.saving = false
        this.toast('error', 'No se pudo aplicar', worklogErrorMessage(e))
      },
    })
  }

  // ─────────── Buscador por folio ───────────

  /**
   * Abre la tarea por su folio, esté o no en este sprint. Es la pregunta de todos los días —«¿en
   * qué va la F-098?»— y la respuesta estaba repartida entre el Tablero, el Backlog y el Historial.
   */
  buscarFolio(): void {
    const folio = normalizeFolio(this.folioQuery)
    if (!folio) {
      this.toast('warn', 'Folio no válido', 'Escribe un folio como F-098 o B-007.')
      return
    }
    this.buscandoFolio = true
    this.worklog.getItemByCode(folio).subscribe((item) => {
      this.buscandoFolio = false
      if (!item) {
        this.toast('warn', 'Sin resultados', `No existe una tarea con el folio ${folio}.`)
        return
      }
      this.folioQuery = ''
      this.openItem.emit(item._id)
    })
  }

  private toast(severity: string, summary: string, detail: string): void {
    this.messageService.add({ key: 'worklog', severity, summary, detail, life: 4000 })
  }
}
